import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { GetChannelRequest } from '@/modules/manage-player/type'
import { useState } from 'react'
import { PlusIcon } from 'lucide-react'
import useQueryGetChannels from '@/modules/manage-player/api/hooks/useQueryGetChannels'
import Spinner from '@/common/components/Spinner'
import useMutationCreatePlayer from '@/modules/manage-player/api/hooks/useMutationCreatePlayer'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { PlayerQueryKeys } from '@/modules/manage-player/keys'
import { useQueryClient } from '@tanstack/react-query'
import { handlePlayerFormError } from '@/modules/manage-player/utils/handlePlayerFormError'
import { BankSelector } from '@/modules/bank/components/BankSelector'
import { RequiredIndicator } from '@/common/ui/form/required-indicator'

const formSchema = z.object({
	phone: z.string(),
	password: z
		.string()
		.min(4, 'รหัสผ่านต้องมี 4 ตัวอักษร')
		.max(4, 'รหัสผ่านต้องมี 4 ตัวอักษร')
		.refine((val) => val.length === 4, 'รหัสผ่านต้องมี 4 ตัวอักษร'),
	bankProvider: z.string(),
	accountName: z.string().min(1, 'กรุณากรอกชื่อบัญชี'),
	accountNumber: z.string().min(1, 'กรุณากรอกเลขบัญชี'),
})

type FormValues = z.infer<typeof formSchema>

interface PlayerFormModalProps {
	trigger?: React.ReactNode
}

export const PlayerFormModal = ({ trigger }: PlayerFormModalProps) => {
	const [open, setOpen] = useState(false)
	const queryClient = useQueryClient()

	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			phone: '',
			password: '',
			bankProvider: '',
			accountName: '',
			accountNumber: '',
		},
	})

	const { mutate: createPlayer, isPending: isCreating } =
		useMutationCreatePlayer()

	const onSubmit = (values: FormValues) => {
		try {
			createPlayer(values, {
				onSuccess: () => {
					toast.success('เพิ่มผู้ใช้งานเรียบร้อยแล้ว')
					queryClient.invalidateQueries({
						queryKey: [PlayerQueryKeys.GetPlayerList],
					})
					form.reset()
					setOpen(false)
				},
				onError: (error) => handlePlayerFormError(error, form, 'create'),
			})
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	const defaultTrigger = (
		<Button variant="default">
			<PlusIcon className="h-4 w-4" />
			เพิ่มผู้ใช้งาน
		</Button>
	)

	const payload: GetChannelRequest = {
		take: 1000,
		skip: 0,
	}

	const { data: channelList, isLoading: isChannelListLoading } =
		useQueryGetChannels(payload)

	if (isChannelListLoading || !channelList) return <Spinner />

	return (
		<>
			<div onClick={() => setOpen(true)}>{trigger || defaultTrigger}</div>

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="sm:max-w-[600px]">
					<DialogHeader>
						<DialogTitle>เพิ่มผู้ใช้งาน</DialogTitle>
					</DialogHeader>

					<Form {...form}>
						<form
							onSubmit={form.handleSubmit(onSubmit)}
							className="max-h-[70vh] space-y-4 overflow-y-auto pr-2"
						>
							<FormField
								control={form.control}
								name="phone"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											<RequiredIndicator /> เบอร์โทรศัพท์
										</FormLabel>
										<FormControl>
											<Input {...field} placeholder="กรอกเบอร์โทรศัพท์" />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="password"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											<RequiredIndicator /> รหัสผ่านสมาชิก (เลข 4 ตัว)
										</FormLabel>
										<FormControl>
											<Input
												type="password"
												inputMode="numeric"
												pattern="[0-9]*"
												placeholder="ตัวเลข 4 ตัว"
												maxLength={4}
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<BankSelector control={form.control} label="ธนาคาร" />

							<FormField
								control={form.control}
								name="accountNumber"
								render={({ field }) => {
									const selectedBank = form.watch('bankProvider')
									const isTrueMoney = selectedBank === 'TrueMoney'
									const label = isTrueMoney ? 'เบอร์โทรศัพท์' : 'เลขบัญชี'
									const placeholder = isTrueMoney
										? 'กรอกเบอร์โทรศัพท์'
										: 'กรอกเลขบัญชี'

									return (
										<FormItem>
											<FormLabel>
												<RequiredIndicator /> {label}
											</FormLabel>
											<FormControl>
												<Input {...field} placeholder={placeholder} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)
								}}
							/>
							<FormField
								control={form.control}
								name="accountName"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											<RequiredIndicator /> ชื่อบัญชี
										</FormLabel>
										<FormControl>
											<Input {...field} placeholder="กรอกชื่อบัญชี" />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<div className="flex justify-end gap-4 pt-4">
								<Button
									variant="outline"
									onClick={() => setOpen(false)}
									type="button"
								>
									ยกเลิก
								</Button>
								<Button type="submit" variant="default" disabled={isCreating}>
									{isCreating ? 'กำลังบันทึก...' : 'บันทึก'}
								</Button>
							</div>
						</form>
					</Form>
				</DialogContent>
			</Dialog>
		</>
	)
}
